import { LOCAL_API_ORIGIN } from './localApi';
import { isWeb } from './platform';

import { getServerUrl, getUpdateChannel } from './updateChannel';
const MATCHMAKING_URL = () => getServerUrl();

// In dev mode, the CEF app and the browser share the same origin
// (127.0.0.1:4173) and therefore the same localStorage. We namespace
// the token key so each environment has its own login session. The CEF
// renderer URL includes ?runtime=cef (set in package.json); browser
// sessions don't have that parameter.
//
// In production, there's no ?runtime=cef. Electrobun loads the
// renderer from a random port. We detect production by checking
// whether Vite's dev flag is absent: if import.meta.env.DEV is false,
// we're in a built app and always persist to disk.
const IS_DEV = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
const IS_CEF_DEV = IS_DEV && typeof location !== 'undefined' && new URLSearchParams(location.search).get('runtime') === 'cef';
const IS_BROWSER_DEV = IS_DEV && !IS_CEF_DEV;
const TOKEN_KEY = IS_CEF_DEV ? 'valkenhall-token-cef' : 'valkenhall-token';

// GUI-test auth injection (dev builds only). The Playwright GUI flow suite
// launches Electron with VALKENHALL_E2E_TOKEN, which the main process
// forwards as ?e2eToken=… on the renderer URL (unpackaged builds only).
// Seed it into localStorage at module init. BEFORE the app's first
// getStoredToken(). So boot auth uses the minted test session and never
// falls back to (or clears) the developer's on-disk token.
try {
  if (typeof location !== 'undefined') {
    const params = new URLSearchParams(location.search);
    if (params.get('dev') === '1') {
      const injected = params.get('e2eToken');
      if (injected) localStorage.setItem(TOKEN_KEY, injected);
    }
  }
} catch { /* no-op. Never block auth on a malformed URL */ }

// Per-channel token cache. Stable and beta point at different
// Postgres instances so each has its own session token. Caching them
// per channel means flipping the Release Channel toggle in Settings
// can swap to the cached token instantly instead of forcing a magic-
// code re-login every time. The primary TOKEN_KEY above always
// mirrors whichever channel is currently active so existing callers
// don't need to know about the cache.
const CHANNEL_PREFIX = IS_CEF_DEV ? 'valkenhall-token-cef:' : 'valkenhall-token:';
function channelKey(channel) {
  return `${CHANNEL_PREFIX}${channel || 'stable'}`;
}
export function setChannelToken(channel, token) {
  try { localStorage.setItem(channelKey(channel), token); } catch {}
}
export function getChannelToken(channel) {
  try { return localStorage.getItem(channelKey(channel)); } catch { return null; }
}
export function clearChannelToken(channel) {
  try { localStorage.removeItem(channelKey(channel)); } catch {}
}
export function clearAllChannelTokens() {
  try {
    for (const ch of ['stable', 'beta', 'local']) {
      localStorage.removeItem(channelKey(ch));
    }
  } catch {}
}

/**
 * Thrown when `fetch` itself fails (DNS failure, TCP refused, TLS
 * handshake timeout, client offline) as opposed to the server
 * returning an HTTP error response. Callers distinguish these so the
 * UI can say "we can't reach the servers" instead of leaking the
 * browser's raw "Failed to fetch" / "Load failed" / "NetworkError
 * when attempting to fetch resource" strings.
 *
 * Also used by app-level flows (checkAuth) to keep the stored token
 * and show a reconnecting banner rather than logging the player out
 * on a transient outage.
 */
export class NetworkError extends Error {
  constructor(cause) {
    super("Can't reach Valkenhall right now. Please check your connection and try again in a moment.");
    this.name = 'NetworkError';
    this.isNetworkError = true;
    if (cause) this.cause = cause;
  }
}

export function isNetworkError(err) {
  return !!(err && err.isNetworkError);
}

// Shared helper that normalises every auth-adjacent fetch into two
// outcomes: a parsed JSON body on success, or a thrown Error. Network
// failures become NetworkError (with the friendly message above);
// HTTP failures become plain Error carrying the server's `error`
// string when present, falling back to a user-friendly default.
async function apiFetch(path, init = {}, { defaultErrorMessage = 'The server returned an unexpected error. Please try again.' } = {}) {
  let res;
  try {
    res = await fetch(`${MATCHMAKING_URL()}${path}`, init);
  } catch (err) {
    // fetch throws TypeError on any transport-level failure. We treat
    // it uniformly as "server unreachable" so the caller can show a
    // connection-themed message instead of browser-specific jargon.
    throw new NetworkError(err);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || defaultErrorMessage);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function requestLoginCode(email, { checkOnly = false } = {}) {
  return apiFetch('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, checkOnly }),
  }, { defaultErrorMessage: 'We could not send a login code. Please try again in a moment.' });
}

// Pings /auth/capacity purely for its `startedAt` side-effect: the
// maintenance overlay uses the server's boot timestamp as the
// "before-redeploy" snapshot so it can detect when a new container
// has come up. The endpoint no longer reports a signup cap.
export async function recordServerStartedAt() {
  try {
    const body = await apiFetch('/auth/capacity', { method: 'GET' });
    try {
      const mod = await import('./maintenanceState');
      mod.recordStartedAt?.(body?.startedAt);
    } catch (_) {}
    return body;
  } catch {
    return null;
  }
}

export async function verifyLoginCode(email, code) {
  return apiFetch('/auth/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  }, { defaultErrorMessage: 'Verification failed. Please double-check the code and try again.' });
}

export async function getMyInviteCode(token) {
  try {
    const data = await apiFetch('/invites/my-code', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return data.code || null;
  } catch {
    return null;
  }
}

// Returns one of:
//   { valid: true, profile }. Token is good
//   { valid: false }. Token is bad (or server said so)
//   throws NetworkError. Couldn't reach the server at all;
//                                    caller should KEEP the token and
//                                    retry, not log the user out.
export async function validateToken(token) {
  try {
    return await apiFetch('/auth/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
  } catch (err) {
    if (isNetworkError(err)) throw err;
    return { valid: false };
  }
}

export async function getStoredToken() {
  // localStorage is namespaced by TOKEN_KEY so browser and CEF dev
  // instances stay isolated even though they share the same origin.
  // Falls back to the on-disk token (needed in production where CEF's
  // port changes per launch, which wipes localStorage).
  try {
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (localToken) return localToken;
  } catch {}
  // No disk mirror on web; localStorage is the only store.
  if (isWeb()) return null;
  try {
    const res = await fetch(`${LOCAL_API_ORIGIN}/api/auth/token`);
    if (res.ok) {
      const { token } = await res.json();
      if (token) return token;
    }
  } catch {}
  return null;
}

export async function setStoredToken(token) {
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
  // Mirror into the per-channel cache so a later channel-switch can
  // resume the same session without a magic-code round-trip.
  setChannelToken(getUpdateChannel(), token);
  // Persist to disk in production (always) and in CEF dev mode.
  // Browser dev sessions must NOT write to disk or they'll clobber
  // the CEF user's session during dual-login testing.
  if (!IS_BROWSER_DEV && !isWeb()) {
    try {
      await fetch(`${LOCAL_API_ORIGIN}/api/auth/token`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    } catch {}
  }
}

// Explicit logout. Wipes the active token AND every per-channel
// cache so the user is fully signed out everywhere. Channel-swap
// flows that only want to swap session (not log out) call
// `setChannelToken` / `setStoredToken` directly instead.
export async function clearStoredToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
  clearAllChannelTokens();
  if (!IS_BROWSER_DEV && !isWeb()) {
    try { await fetch(`${LOCAL_API_ORIGIN}/api/auth/token`, { method: 'DELETE' }); } catch {}
  }
}
